import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI, onboardingAPI } from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  // iter431 — ordered talent onboarding state. Null until fetched.
  const [onboarding, setOnboarding] = useState(null);

  // iter431 — fetch the authoritative ordered onboarding state for talent.
  // Safe no-op for non-talent. Never blocks the UI.
  const refreshOnboarding = async (currentUser) => {
    const u = currentUser || user;
    if (!u || u.role !== 'talent') {
      setOnboarding(null);
      return null;
    }
    try {
      const res = await onboardingAPI.getStatus();
      setOnboarding(res.data);
      return res.data;
    } catch (err) {
      console.warn('[onboarding] status fetch failed:', err?.message);
      return null;
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('token');
      const storedUser = localStorage.getItem('user');

      if (storedToken && storedUser) {
        // Iter-180 P0 — never block app boot on a slow mobile getMe().
        // We hydrate from localStorage *synchronously*, mark loading
        // complete, and then refresh in the background.
        try {
          setToken(storedToken);
          setUser(JSON.parse(storedUser));
        } catch {
          logout();
        }
        setLoading(false);

        // Background refresh — top up profile fields without ever
        // blocking the UI.
        //
        // Iter-292e — Deployment stability. Previously a transient 401
        // from a restarting backend kicked the user out. Now we only
        // logout when the response BODY explicitly says the token is
        // dead (token_expired / invalid_token / session_expired). Any
        // other failure (network error, 502/503/504, bare 401) is
        // treated as "backend warming up" — the api.js interceptor
        // will retry /auth/me with backoff and the user stays logged
        // in.
        authAPI.getMe()
          .then(response => {
            setUser(response.data);
            localStorage.setItem('user', JSON.stringify(response.data));
          })
          .catch(error => {
            const status = error?.response?.status;
            const detail = String(error?.response?.data?.detail || '').toLowerCase();
            const tokenDead = (
              detail.includes('token has expired') ||
              detail.includes('invalid token') ||
              detail.includes('session has expired') ||
              detail.includes('token_expired') ||
              detail.includes('invalid_token') ||
              detail.includes('session_expired') ||
              detail.includes('logged in on another device')
            );
            if ((status === 401 || status === 403) && tokenDead) {
              logout();
            } else {
              console.warn('[auth] init getMe transient failure (will retry):', status, detail || error?.message);
            }
          });
        return;
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // iter431 — keep onboarding state in sync whenever the talent user changes.
  useEffect(() => {
    if (user && user.role === 'talent') {
      refreshOnboarding(user);
    } else {
      setOnboarding(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.role, user?.phone_verified, user?.idv_status]);

  const login = async (email, password) => {
    // Iter-180 P0 — Mobile login freeze fix.
    //
    // Previous flow chained `authAPI.login()` → `authAPI.getMe()` which
    // meant a slow second request (common on flaky mobile data) would
    // leave the user stuck on the spinner indefinitely. Now we:
    //   1. Authenticate and store the token.
    //   2. Hydrate user state from the login response immediately
    //      (role + email + user_id are enough for routing/role gates).
    //   3. Fire `getMe()` in the background to top up admin_role +
    //      admin_permissions.
    // If `getMe()` is slow or fails, the user still lands on their
    // dashboard. Admin-permission-gated UI just rehydrates a moment later.
    const response = await authAPI.login({ email, password });
    // Iter-313k — Verification gating. Store idv_status (talent) and
    // business_verification (client/agency) so ProtectedRoute + Login
    // can redirect immediately without an extra round-trip.
    const {
      token: newToken, user_id, email: userEmail, role, client_approval_status,
      idv_status, business_verification, phone_verified, phone_verification_required,
      payment_hold,
    } = response.data;

    // Iter-256 — Defensive wipe of stale impersonation state on every fresh
    // login. Previously a logged-out admin who was impersonating could leave
    // `impersonating=true` + original_token in localStorage if their session
    // ended via token-expiry (instead of clicking the explicit Logout button).
    // The red banner would then re-appear after the next login with the new
    // user account — which the user (correctly) read as a stale "Incognito"
    // banner. Always clear it here so logging in starts from a clean slate.
    localStorage.removeItem('impersonating');
    localStorage.removeItem('impersonated_user_email');
    localStorage.removeItem('original_token');
    localStorage.removeItem('original_user');
    if (typeof document !== 'undefined') {
      document.body.removeAttribute('data-impersonating');
    }

    localStorage.setItem('token', newToken);
    // Iter-272 — Stamp the login timestamp so the API interceptor can
    // suppress auto-logout during the 30s JWT propagation grace window.
    // Fixes the "double login required" + "kicked out mid-navigation" P0.
    localStorage.setItem('_last_login_at', String(Date.now()));
    const partialUser = {
      id: user_id, email: userEmail, role,
      client_approval_status: client_approval_status || 'approved',
      idv_status: idv_status || null,
      business_verification: business_verification || null,
      phone_verified: phone_verified === true,
      phone_verification_required: phone_verification_required === true,
      payment_hold: payment_hold === true,
      admin_role: null, admin_permissions: {},
    };
    localStorage.setItem('user', JSON.stringify(partialUser));
    setToken(newToken);
    setUser(partialUser);

    // Background top-up — never blocks the redirect.
    authAPI.getMe()
      .then(meResponse => {
        const fullUser = {
          ...partialUser,
          admin_role: meResponse.data.admin_role || null,
          admin_permissions: meResponse.data.admin_permissions || {},
          phone_verification_required: meResponse.data.phone_verification_required === true,
          payment_hold: meResponse.data.payment_hold === true,
        };
        localStorage.setItem('user', JSON.stringify(fullUser));
        setUser(fullUser);
      })
      .catch(err => console.warn('[auth] getMe background refresh failed:', err?.message));

    return partialUser;
  };

  // Iter-411s — P0 Google Login Stranded fix.
  //
  // Prior state: `AuthCallback` stored the token in localStorage,
  // called a non-existent `setAuth` (silently no-op via ?. chaining),
  // showed "Welcome back!" and navigated to /talent/dashboard. The
  // ProtectedRoute read `user === null` from context and bounced back
  // to /login. User stranded on welcome screen after Google OAuth.
  //
  // This helper is the ONLY sanctioned way to complete a social sign-in.
  // It:
  //   1. Persists token + partial user snapshot to localStorage.
  //   2. Sets React state synchronously.
  //   3. Fetches /me to enrich admin_role, admin_permissions,
  //      idv_status, client_approval_status.
  //   4. Returns the fully-hydrated user (or throws on hard failure so
  //      the caller can render a recoverable error UI).
  //
  // Only after this resolves may the caller show "Welcome back!" and
  // route the user to a role-specific dashboard.
  const loginWithSocial = async ({ token: newToken, user: socialUser }) => {
    if (!newToken || !socialUser?.id) {
      throw new Error('Social login response is missing token or user');
    }

    // Clear any impersonation cruft from a previous session.
    localStorage.removeItem('impersonating');
    localStorage.removeItem('impersonated_user_email');
    localStorage.removeItem('original_token');
    localStorage.removeItem('original_user');
    if (typeof document !== 'undefined') {
      document.body.removeAttribute('data-impersonating');
    }

    // Persist token + stamp login time BEFORE hydrating so the api.js
    // interceptor has grace-window context if /me is slow.
    localStorage.setItem('token', newToken);
    localStorage.setItem('_last_login_at', String(Date.now()));

    // Seed a minimum-viable user so ProtectedRoute passes even if /me
    // is slow (mirrors Login flow).
    const partialUser = {
      id: socialUser.id,
      email: socialUser.email,
      role: socialUser.role,
      client_approval_status: 'approved',
      idv_status: null,
      business_verification: null,
      admin_role: null,
      admin_permissions: {},
    };
    localStorage.setItem('user', JSON.stringify(partialUser));
    setToken(newToken);
    setUser(partialUser);

    // Await /me to get full verification + role state. This is what
    // the callback must WAIT on before showing success + routing.
    // Failure here is recoverable — the caller decides whether to
    // retry, sign out, or continue with the partial user.
    let fullUser = partialUser;
    try {
      const meResponse = await authAPI.getMe();
      fullUser = {
        ...partialUser,
        ...meResponse.data,
        // Preserve id in the shape ProtectedRoute expects.
        id: meResponse.data.id || meResponse.data.user_id || partialUser.id,
        admin_role: meResponse.data.admin_role || null,
        admin_permissions: meResponse.data.admin_permissions || {},
      };
      localStorage.setItem('user', JSON.stringify(fullUser));
      setUser(fullUser);
    } catch (err) {
      // Bootstrap failed. Surface the error to the caller so it can
      // render Retry / Sign out & retry UI instead of leaving the user
      // stranded on the welcome screen. Token is still persisted so a
      // retry does not re-do the OAuth handshake.
      const status = err?.response?.status;
      const detail = String(err?.response?.data?.detail || err?.message || '');
      const bootErr = new Error(`Bootstrap failed: ${detail || 'network error'}`);
      bootErr.code = 'BOOTSTRAP_FAILED';
      bootErr.status = status;
      throw bootErr;
    }

    return fullUser;
  };

  const register = async (data) => {
    const response = await authAPI.register(data);
    // Iter-313k — Verification gating on registration flow too.
    const {
      token: newToken, user_id, email, role, client_approval_status,
      idv_status, business_verification,
    } = response.data;

    localStorage.setItem('token', newToken);
    localStorage.setItem('_last_login_at', String(Date.now()));  // Iter-272
    const userData = {
      id: user_id, email, role,
      client_approval_status: client_approval_status || 'approved',
      idv_status: idv_status || null,
      business_verification: business_verification || null,
    };
    localStorage.setItem('user', JSON.stringify(userData));

    setToken(newToken);
    setUser(userData);

    return userData;
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('_last_login_at');  // Iter-272
    // Iter-241 — Clear any impersonation state on logout. Previously a logged-out
    // admin who was impersonating still had `impersonating=true` + original_token
    // in localStorage, causing the red banner to reappear after the next login.
    localStorage.removeItem('impersonating');
    localStorage.removeItem('impersonated_user_email');
    localStorage.removeItem('original_token');
    localStorage.removeItem('original_user');
    if (typeof document !== 'undefined') {
      document.body.removeAttribute('data-impersonating');
    }
    setToken(null);
    setUser(null);
  };

  // iter429 — re-fetch /auth/me and update state+storage. Used by the
  // phone-verification gate (and any flow) to pick up freshly-updated
  // verification/approval state without a full page reload.
  const refreshUser = async () => {
    try {
      const meResponse = await authAPI.getMe();
      const fullUser = {
        ...(user || {}),
        ...meResponse.data,
        id: meResponse.data.id || meResponse.data.user_id || user?.id,
      };
      localStorage.setItem('user', JSON.stringify(fullUser));
      setUser(fullUser);
      return fullUser;
    } catch (err) {
      console.warn('[auth] refreshUser failed:', err?.message);
      return user;
    }
  };

  // Helper function to check admin permission
  const hasAdminPermission = (permission) => {
    if (!user || user.role !== 'admin') return false;
    return user.admin_permissions?.[permission] === true;
  };

  // Check if user is super admin (has all permissions)
  const isSuperAdmin = () => {
    return user?.admin_role === 'super_admin';
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      token, 
      loading, 
      login, 
      loginWithSocial,
      register, 
      logout,
      refreshUser,
      onboarding,
      refreshOnboarding,
      hasAdminPermission,
      isSuperAdmin
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;